import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line, Doughnut } from 'react-chartjs-2';
import './Dashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

function KpiCard({ title, value, icon, color }) {
  return (
    <div className="kpi-card" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="kpi-icon" style={{ color }}>{icon}</div>
      <div className="kpi-content">
        <div className="kpi-title">{title}</div>
        <div className="kpi-value">{value}</div>
      </div>
    </div>
  );
}



function formatPercentage(value) {
  if (value == null) return '0.00%';
  return `${parseFloat(value).toFixed(2)}%`;
}

function CompanyDashboard() {
  const [stats, setStats] = useState(null);
  const [revenueChartData, setRevenueChartData] = useState(null);
  const [expenseChartData, setExpenseChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const gridColor = 'rgba(0,0,0,0.05)';
  const tickColor = '#6b7280';

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = () => {
    setLoading(true);
    axios.get('/dashboard/consolidated')
      .then(res => {
        const data = res.data;
        setStats(data);
        setRevenueChartData(prepareRevenueChartData(data.revenueChartData));
        setExpenseChartData(prepareExpenseChartData(data.expenseChartData));
        setLoading(false);
      })
      .catch(err => {
        setError('Failed to load consolidated dashboard data.');
        setLoading(false);
      });
  };

  const prepareRevenueChartData = (data) => {
    if (!data || !data.labels || !data.datasets) return null;
    return {
      labels: data.labels,
      datasets: data.datasets.map(dataset => ({
        label: dataset.label,
        data: dataset.data,
        borderColor: '#e8590c',
        backgroundColor: 'rgba(232,89,12,0.1)',
        pointBackgroundColor: '#e8590c',
        pointRadius: 4,
        tension: 0.4,
        fill: true,
      }))
    };
  };

  const prepareExpenseChartData = (data) => {
    if (!data || !data.labels || !data.datasets) return null;
    const colors = [
      '#e8590c', '#6366f1', '#10b981', '#f59e0b', '#3b82f6', '#ec4899'
    ];
    return {
      labels: data.labels,
      datasets: data.datasets.map(dataset => ({
        label: dataset.label,
        data: dataset.data,
        backgroundColor: colors,
        borderColor: '#fff',
        borderWidth: 2,
      }))
    };
  };

  if (loading) return <div className="dashboard-loading">Loading dashboard...</div>;
  if (error) return <div className="dashboard-error">{error}</div>;

  const profitLoss = stats?.profitLoss ?? 0;
  const profitColor = profitLoss >= 0 ? '#10b981' : '#ef4444';

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Company Overview</h1>
        <p className="dashboard-subtitle">Consolidated view across all branches (read-only)</p>
      </div>

      {/* KPI Cards Grid */}
      <div className="kpi-grid">
        <KpiCard
          title="Total Revenue"
          value={formatCurrency(stats?.totalRevenue)}
          icon="💰"
          color="#3b82f6"
        />
        <KpiCard
          title="Total Expenses"
          value={formatCurrency(stats?.totalExpenses)}
          icon="💸"
          color="#f59e0b"
        />
        <KpiCard
          title="Profit / Loss"
          value={formatCurrency(profitLoss)}
          icon={profitLoss >= 0 ? '📈' : '📉'}
          color={profitColor}
        />
        <KpiCard
          title="Profit Margin"
          value={formatPercentage(stats?.profitMargin)}
          icon="📊"
          color="#10b981"
        />
        <KpiCard
          title="Outstanding vendor balance"
          value={formatCurrency(stats?.outstandingAmount)}
          icon="⚠️"
          color="#ef4444"
        />
        <KpiCard
          title="Active Projects"
          value={stats?.activeProjectCount ?? 0}
          icon="🏗"
          color="#8b5cf6"
        />
        <KpiCard
          title="Total Employees"
          value={stats?.totalEmployeeCount ?? 0}
          icon="👥"
          color="#06b6d4"
        />
        <KpiCard
          title="Branches"
          value={stats?.branchCount ?? 0}
          icon="🏢"
          color="#ec4899"
        />
      </div>

      {/* Consolidated Charts */}
      <div className="charts-grid">
        <div className="chart-card">
          <h2>Consolidated Revenue Trend</h2>
          <div className="chart-container">
            {revenueChartData ? (
              <Line 
                data={revenueChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    x: {
                      grid: { display: false },
                      ticks: { color: tickColor, font: { size: 11 } },
                    },
                    y: {
                      beginAtZero: true,
                      grid: { color: gridColor },
                      ticks: {
                        color: tickColor,
                        font: { size: 11 },
                        callback: (v) => getCurrencySymbol() + v.toLocaleString(sessionStorage.getItem('companyCountry') === 'India' ? 'en-IN' : 'en-US'),
                      }
                    }
                  }
                }}
              />
            ) : (
              <div className="no-data">No consolidated revenue data available</div>
            )}
          </div>
        </div>

        <div className="chart-card">
          <h2>Consolidated Expense Breakdown</h2>
          <div className="chart-container">
            {expenseChartData ? (
              <Doughnut 
                data={expenseChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutout: '65%',
                  plugins: {
                    legend: {
                      position: 'right',
                      labels: { color: tickColor, font: { size: 11 }, boxWidth: 12 }
                    },
                    tooltip: {
                      callbacks: {
                         label: (ctx) => `${ctx.label}: ${getCurrencySymbol()}${ctx.parsed.toLocaleString(sessionStorage.getItem('companyCountry') === 'India' ? 'en-IN' : 'en-US')}`
                      }
                    }
                  }
                }}
              />
            ) : (
              <div className="no-data">No consolidated expense data available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyDashboard;
